import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import {
  Home, FilePlus2, FilePen, Search, CalendarDays, CalendarRange, Tags, Wallet,
  FileStack, Truck, Users2, Settings, LogOut, Bell, Menu as MenuIcon, ChevronDown, X, Building2, Smartphone, HelpCircle, Megaphone, Inbox, Network, Receipt, Printer, Mail, FileSpreadsheet, KeyRound, Package
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { PC_LOGO_URL, api } from '@/lib/api';
import { Btn } from '@/components/ui-ios';
import AnnouncementModal from '@/components/AnnouncementModal';
import InstallAppButton from '@/components/InstallAppButton';
import TutorialTour from '@/components/TutorialTour';
import SystemStatsAlert from '@/components/SystemStatsAlert';
import ChangePasswordDialog from '@/components/ChangePasswordDialog';
import { toast } from 'sonner';

/**
 * AppShellTopNav - main chrome with horizontal top-centered navigation.
 */

function NavDropdown({ label, icon: Icon, items, testId, dataTour }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)} data-tour={dataTour}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className={`flex items-center gap-1.5 px-3 h-9 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${open ? 'bg-[color:var(--brand-orange)] text-white' : 'text-zinc-700 hover:bg-zinc-100'}`}
        data-testid={testId || `topnav-${label.toLowerCase()}-button`}
      >
        {Icon && <Icon className="h-4 w-4" />}
        <span>{label}</span>
        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute left-1/2 -translate-x-1/2 top-full pt-1 z-40 min-w-[240px]">
          <div className="bg-white rounded-2xl shadow-[var(--shadow-pop)] border border-[color:var(--hairline)] p-1.5">
            {items.map(it => {
              const I = it.icon;
              const key = (it.to || it.label || '') + (it.label || '');
              // Items can either navigate to a route (it.to) or fire a custom
              // action (it.onClick) — used e.g. for "Stampa Avviso Giacenza"
              // which triggers a PDF download instead of a navigation.
              if (it.onClick && !it.to) {
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => { setOpen(false); it.onClick(); }}
                    className="w-full text-left flex items-center gap-2 px-3 py-2 rounded-xl text-sm transition-colors whitespace-nowrap hover:bg-[color:var(--surface-2)] text-zinc-800"
                    data-testid={it.testid || `topnav-action-${(it.label || '').toLowerCase().replace(/[^a-z0-9]+/gi,'-')}`}
                  >
                    {I && <I className="h-4 w-4 shrink-0" />}
                    <span className="truncate">{it.label}</span>
                  </button>
                );
              }
              return (
                <NavLink
                  key={key}
                  to={it.to}
                  end={it.end}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) => `flex items-center gap-2 px-3 py-2 rounded-xl text-sm transition-colors whitespace-nowrap ${isActive ? 'bg-[color:var(--brand-orange)] text-white' : 'hover:bg-[color:var(--surface-2)] text-zinc-800'}`}
                  data-testid={`topnav-item-${it.to.replace(/[^a-z0-9]+/gi,'-')}`}
                >
                  {I && <I className="h-4 w-4 shrink-0" />}
                  <span className="truncate">{it.label}</span>
                </NavLink>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function NavSingle({ to, label, icon: Icon, end, dataTour }) {
  return (
    <NavLink
      to={to}
      end={end}
      data-tour={dataTour}
      className={({ isActive }) => `flex items-center gap-1.5 px-3 h-9 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${isActive ? 'bg-[color:var(--brand-orange)] text-white' : 'text-zinc-700 hover:bg-zinc-100'}`}
      data-testid={`topnav-${label.toLowerCase().replace(/[^a-z0-9]+/g,'-')}`}
    >
      {Icon && <Icon className="h-4 w-4" />}
      <span>{label}</span>
    </NavLink>
  );
}

export default function AppShellTopNav() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [changePwOpen, setChangePwOpen] = useState(false);
  const isSA = user?.role === 'superadmin';

  // Multi-tenant branding: each agency can have its own logo + display name.
  // Fall back to the global PostaCentrale logo when not configured.
  const customLogoUrl = user?.branding?.logo_data_url?.trim();
  const brandDisplayName =
    user?.branding?.brand_display_name?.trim() ||
    user?.branding?.ragione_sociale?.trim() ||
    '';
  const useCustomLogo = !!customLogoUrl;
  const logoSrc = useCustomLogo ? customLogoUrl : PC_LOGO_URL;
  const logoAlt = useCustomLogo ? (brandDisplayName || 'Logo agenzia') : 'PostaCentrale';

  const raccomandateItems = [
    { to: '/raccomandate/nuova', label: 'Crea nuova raccomandata', icon: FilePlus2 },
    { to: '/raccomandate/cerca?action=modifica', label: 'Modifica Dati Raccomandata', icon: FilePen },
    { to: '/raccomandate/cerca', label: 'Cerca Raccomandata', icon: Search },
    { to: '/raccomandate/ritiro', label: 'Ritiro Raccomandata (Sportello)', icon: Inbox },
    // Posta Massiva — separate flow, but lives in the same menu for
    // discoverability. The page itself acts as new + lista combined.
    { to: '/posta-massiva', label: 'Posta Massiva (Bulk)', icon: Mail },
    // Bulk import from CSV / XLSX — for both raccomandate and posta massiva.
    { to: '/importa', label: 'Importa da CSV / Excel', icon: FileSpreadsheet },
  ];
  const totaliItems = [
    { to: '/totali/giornaliero', label: 'Totale Giornaliero', icon: CalendarDays },
    { to: '/totali/mensile', label: 'Totale Mensile', icon: CalendarRange },
  ];
  const bolgettaItems = [
    { to: '/moduli/accettazione-bolgetta', label: 'Accettazione Bolgetta', icon: FileStack },
    { to: '/moduli/distinta-analitica', label: 'Distinta Analitica', icon: FileStack },
  ];
  const onPrintAvvisoGiacenza = async () => {
    // Fetch the Avviso di Giacenza module PDF using the authenticated session
    // and open it in a new tab with the system print dialog (analogous to
    // ricevuta / barcode flow). The PDF is generated dynamically from the
    // agency branding (nome, indirizzo, orari, etc.).
    try {
      const token = localStorage.getItem('pc_token');
      const resp = await fetch(`${api.defaults.baseURL}/giacenza-modulo-pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) { toast.error('Errore generazione modulo'); return; }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const w = window.open(url, '_blank');
      if (w) {
        w.addEventListener('load', () => {
          try { w.focus(); w.print(); } catch (e) { /* popup may not allow scripted print */ }
        });
      } else {
        const a = document.createElement('a');
        a.href = url; a.download = 'modulo-avviso-giacenza.pdf';
        document.body.appendChild(a); a.click(); a.remove();
        toast.info('Popup bloccato: PDF scaricato');
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      toast.success('Modulo Avviso Giacenza pronto per la stampa');
    } catch (e) {
      toast.error('Errore generazione PDF');
    }
  };

  const altroItems = [
    { to: '/prezzi', label: 'Modifica Prezzi Vendita', icon: Tags },
    ...(isSA ? [{ to: '/costi-agenzia', label: 'Costo Raccomandate AGENZIA', icon: Wallet }] : []),
    ...(isSA ? [{ to: '/impostazioni/corrieri', label: 'Corrieri', icon: Truck }] : []),
    { to: '/portalettere', label: 'Portalettere', icon: Users2 },
    ...(isSA ? [{ to: '/admin/utenti', label: 'Utenti agenzia', icon: Users2 }] : []),
    ...(isSA ? [{ to: '/admin/pezzi-agenzie', label: 'Pezzi per agenzia', icon: Package }] : []),
    ...(isSA ? [{ to: '/admin/fatture-rete', label: 'Emissione fatture rete', icon: Receipt }] : []),
    ...(!isSA ? [{ to: '/fatturazione-rete', label: 'Fatturazione rete PostaCentrale', icon: Network }] : []),
    ...(isSA ? [{ to: '/admin/annunci', label: 'Annunci & Notizie', icon: Megaphone }] : []),
    { onClick: onPrintAvvisoGiacenza, label: 'Stampa Avviso Giacenza', icon: Printer, testid: 'topnav-stampa-avviso-giacenza' },
    { to: '/impostazioni', label: 'Impostazioni', icon: Settings },
  ];

  const onLogout = async () => { await logout(); navigate('/login'); };

  const mobileItems = [
    { to: '/', label: 'Home', icon: Home, end: true },
    ...raccomandateItems,
    { to: '/clienti-business', label: 'Clienti Business', icon: Building2 },
    ...totaliItems,
    ...bolgettaItems,
    ...altroItems,
  ];

  return (
    <div className="ios-bg min-h-screen flex flex-col">
      {/* SuperAdmin-only system-load banner (CPU/RAM/Disk warnings) */}
      <SystemStatsAlert />
      {/* Top bar */}
      <header className="topbar-blur sticky top-0 z-30 border-b border-[color:var(--hairline)]" data-testid="topnav-header">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-2.5 flex items-center gap-4">
          {/* Logo */}
          <div className="flex items-center gap-2 shrink-0">
            <button onClick={() => setMobileOpen(o => !o)} className="xl:hidden h-10 w-10 inline-flex items-center justify-center rounded-full hover:bg-zinc-100" data-testid="topnav-mobile-toggle">
              {mobileOpen ? <X className="h-5 w-5" /> : <MenuIcon className="h-5 w-5" />}
            </button>
            <NavLink to="/" className="flex items-center gap-2" data-testid="topnav-logo">
              <img
                src={logoSrc}
                alt={logoAlt}
                className="h-9 sm:h-11 w-auto max-w-[180px] object-contain"
                draggable={false}
                data-testid="topnav-logo-img"
              />
              {useCustomLogo && brandDisplayName && (
                <span className="hidden sm:inline text-sm font-semibold text-zinc-700 truncate max-w-[180px]" data-testid="topnav-brand-name">
                  {brandDisplayName}
                </span>
              )}
            </NavLink>
          </div>

          {/* Center nav */}
          <nav className="hidden xl:flex flex-1 items-center justify-center gap-1" data-testid="topnav-center">
            <NavSingle to="/" end label="Home" icon={Home} dataTour="home" />
            <NavDropdown label="Raccomandate" icon={FilePlus2} items={raccomandateItems} dataTour="raccomandate" />
            <NavSingle to="/clienti-business" label="Clienti Business" icon={Building2} dataTour="clienti-business" />
            <NavDropdown label="Totali" icon={CalendarDays} items={totaliItems} dataTour="totali" />
            <NavDropdown label="Bolgetta" icon={FileStack} items={bolgettaItems} dataTour="bolgetta" />
            <NavDropdown label="Altro" icon={Settings} items={altroItems} dataTour="altro" />
          </nav>
          <div className="hidden xl:block flex-1" />

          {/* Right actions */}
          <div className="flex items-center gap-1.5 shrink-0">
            <InstallAppButton className="hidden lg:inline-flex" />
            <Btn variant="outline" size="sm" onClick={() => navigate('/raccomandate/cerca')} testid="topnav-search-button" className="hidden sm:inline-flex" data-tour="search"><Search className="h-4 w-4" />Cerca</Btn>
            <Btn variant="accent" size="sm" onClick={() => navigate('/raccomandate/nuova')} testid="topnav-new-button" data-tour="new-raccomandata"><FilePlus2 className="h-4 w-4" />Nuova</Btn>
            <button onClick={() => navigate('/m')} className="h-10 w-10 inline-flex items-center justify-center rounded-full hover:bg-zinc-100" title="App PortaLettere (mobile)" data-testid="topnav-portalettere-button" data-tour="mobile-link">
              <Smartphone className="h-5 w-5" />
            </button>
            <button
              onClick={() => { try { window.dispatchEvent(new CustomEvent('pc-start-tutorial')); } catch (e) { /* CustomEvent may be unsupported in legacy browsers */ } }}
              className="h-10 w-10 inline-flex items-center justify-center rounded-full hover:bg-zinc-100 text-zinc-700"
              title="Tutorial / Guida"
              data-tour="help-button"
              data-testid="topnav-help-button"
              aria-label="Avvia tutorial"
            >
              <HelpCircle className="h-5 w-5" />
            </button>
            <button className="h-10 w-10 inline-flex items-center justify-center rounded-full hover:bg-zinc-100" title="Notifiche">
              <Bell className="h-5 w-5" />
            </button>
            <div className="relative group" data-testid="topnav-user-menu">
              <button className="flex items-center gap-2 pr-2 pl-2 h-10 rounded-full hover:bg-zinc-100">
                <div className="h-8 w-8 rounded-full bg-black text-white flex items-center justify-center text-xs font-bold">{(user?.nome || user?.email || '?').slice(0,1).toUpperCase()}</div>
                <div className="hidden md:flex flex-col items-start leading-none">
                  <span className="text-[12px] font-semibold">{user?.nome || user?.email}</span>
                  <span className="text-[10px] text-[color:var(--text-secondary)] uppercase tracking-wide mt-0.5">{user?.role}</span>
                </div>
              </button>
              <div className="absolute right-0 top-full mt-1 w-56 bg-white rounded-2xl shadow-[var(--shadow-pop)] border border-[color:var(--hairline)] p-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all">
                <div className="px-3 py-2 text-xs text-[color:var(--text-secondary)] truncate">{user?.email}</div>
                <button onClick={() => navigate('/impostazioni')} className="w-full text-left px-3 py-2 rounded-xl text-sm hover:bg-zinc-100 flex items-center gap-2" data-testid="user-menu-impostazioni"><Settings className="h-4 w-4" /> Impostazioni</button>
                <button onClick={() => setChangePwOpen(true)} className="w-full text-left px-3 py-2 rounded-xl text-sm hover:bg-zinc-100 flex items-center gap-2" data-testid="user-menu-change-password"><KeyRound className="h-4 w-4" /> Cambia password</button>
                <button onClick={onLogout} className="w-full text-left px-3 py-2 rounded-xl text-sm hover:bg-zinc-100 flex items-center gap-2 text-rose-600" data-testid="user-menu-logout"><LogOut className="h-4 w-4" /> Logout</button>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile collapsible menu */}
        {mobileOpen && (
          <div className="xl:hidden border-t border-[color:var(--hairline)] bg-white">
            <div className="max-w-[1600px] mx-auto px-4 py-3 space-y-1">
              {mobileItems.map(it => {
                const I = it.icon;
                const key = (it.to || it.label) + (it.label || '');
                // Buttons (it.onClick) need a different element than NavLinks
                // (it.to) — keeps the action-style items working in the mobile
                // drawer too.
                if (it.onClick && !it.to) {
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => { setMobileOpen(false); it.onClick(); }}
                      className="w-full text-left flex items-center gap-2 px-3 py-2 rounded-xl text-sm hover:bg-zinc-100 text-zinc-800"
                      data-testid={it.testid || `topnav-mobile-action-${(it.label || '').toLowerCase().replace(/[^a-z0-9]+/gi,'-')}`}
                    >
                      {I && <I className="h-4 w-4" />}
                      <span>{it.label}</span>
                    </button>
                  );
                }
                return (
                  <NavLink key={key} to={it.to} end={it.end} onClick={() => setMobileOpen(false)} className={({isActive}) => `flex items-center gap-2 px-3 py-2 rounded-xl text-sm ${isActive ? 'bg-[color:var(--brand-orange)] text-white' : 'hover:bg-zinc-100 text-zinc-800'}`}>
                    {I && <I className="h-4 w-4" />}
                    <span>{it.label}</span>
                  </NavLink>
                );
              })}
            </div>
          </div>
        )}
      </header>

      <main className="flex-1 max-w-[1600px] mx-auto w-full px-4 sm:px-6 lg:px-8 py-6">
        <Outlet />
      </main>

      <footer className="text-center text-[11px] text-[color:var(--text-secondary)] py-3">
        v1.0 · Uffici di Posta Privata
      </footer>

      {/* Global UX overlays */}
      <AnnouncementModal />
      <TutorialTour />
      <ChangePasswordDialog open={changePwOpen} onOpenChange={setChangePwOpen} />
    </div>
  );
}
